import { SOCIAL_OG_IMAGE, SOCIAL_OG_IMAGE_ALT } from "@/lib/social-image";
import { BlogHero } from "@/components/BlogHero";
import { blogHeroUrl, blogHeroAlt } from "@/data/_media/blog-hero";
import { createFileRoute, Link } from "@tanstack/react-router";
import { ctrDesc, ctrTitle } from "@/lib/seo-copy";
import { SITE } from "@/lib/site-config";
import {
  buildJsonLd,
  jsonLdScript,
  webPageSchema,
  breadcrumbListSchema,
  articleSchema,
  faqPageSchema,
} from "@/lib/schema";
import { canonicalAndHreflang } from "@/lib/hreflang";

/** English mirror of the Thai police clearance certificate guide. */
const TITLE = "Thai Police Clearance Certificate for Use Abroad — Complete Guide";
const DESC =
  "How to obtain a Thai police clearance certificate, the documents required, the certification steps that follow, and the reasons files are returned.";
const PATH = "/blog/police-clearance-certificate-guide";
const URL = `/en${PATH}`;
const PUBLISHED = "2026-08-10";

const FAQ = [
  {
    question: "What is a Thai police clearance certificate?",
    answer:
      "It is an official record issued by the Royal Thai Police stating whether a person has a criminal record in Thailand. Destination authorities request it for visa, residence, employment, and licensing files, and they normally require the original document rather than a copy.",
  },
  {
    question: "Who can apply, and can someone apply on my behalf?",
    answer:
      "Thai nationals and foreigners who have lived in Thailand can apply. An authorised representative can apply on your behalf, but the power of attorney must cover each step: requesting the record, collecting it, submitting it for certification, and lodging it with the embassy. A narrow authority stalls part-way through the chain.",
  },
  {
    question: "Which documents do I need to prepare?",
    answer:
      "Typically a passport or national identity card, house registration or proof of address in Thailand, fingerprint forms, passport photographs, the reason for the request, and supporting documents such as the visa application or employer letter. Any name change must be evidenced by an official record.",
  },
  {
    question: "How long is the certificate valid?",
    answer:
      "The certificate itself does not expire, but destination authorities usually accept it only if issued within three or six months of submission. Plan backwards from your submission date so the document does not fall outside the window while later certification steps are completed.",
  },
  {
    question: "Do I need certification and translation after issue?",
    answer:
      "Usually yes. Most destinations require the certificate to be translated into an accepted language, certified by the Department of Consular Affairs, and then legalised by the destination embassy — or apostilled where the destination accepts that route. Confirm the exact chain before starting.",
  },
  {
    question: "Can I apply from outside Thailand?",
    answer:
      "Yes. Applications can generally be lodged through a Thai embassy or consulate, or through an authorised representative in Thailand. Fingerprints usually have to be taken by a competent authority in the country where you are resident and then sent to Thailand.",
  },
  {
    question: "What causes a file to be returned?",
    answer:
      "Name spellings that differ between passport and Thai records, unclear fingerprints, an out-of-date address record, an expired supporting document, translations that omit seal and stamp text, and certifications performed in the wrong order.",
  },
];

export const Route = createFileRoute("/en/blog/police-clearance-certificate-guide")({
  component: EnPoliceClearanceGuide,
  loader: () => ({
    h: {
      meta: [
        { title: ctrTitle(`${TITLE} — ${SITE.name}`) },
        { name: "description", content: ctrDesc(DESC) },
        { property: "og:title", content: ctrTitle(TITLE) },
        { property: "og:description", content: ctrDesc(DESC) },
        { property: "og:type", content: "article" },
        { property: "og:url", content: `https://${SITE.canonicalHost}${URL}` },
        { property: "og:locale", content: "en" },
        { name: "twitter:card", content: "summary_large_image" },
        { property: "og:image", content: blogHeroUrl("police-clearance-certificate-guide") ?? SOCIAL_OG_IMAGE },
        { property: "og:image:alt", content: blogHeroAlt("police-clearance-certificate-guide", "en") ?? SOCIAL_OG_IMAGE_ALT },
        { name: "twitter:image", content: blogHeroUrl("police-clearance-certificate-guide") ?? SOCIAL_OG_IMAGE },
        {
          name: "keywords",
          content:
            "thai police clearance certificate, police clearance thailand for visa, royal thai police criminal record check, police clearance translation certification, thai police certificate abroad",
        },
      ],
      links: canonicalAndHreflang(PATH, null, "en"),
      scripts: [
        jsonLdScript(
          buildJsonLd([
            webPageSchema({ url: URL, title: TITLE, description: DESC, lastModified: PUBLISHED, speakable: true }),
            breadcrumbListSchema([
              { name: "Home", url: "/en" },
              { name: "Blog", url: "/blog" },
              { name: TITLE, url: URL },
            ]),
            articleSchema({
            image: blogHeroUrl("police-clearance-certificate-guide"),
              url: URL,
              headline: TITLE,
              description: DESC,
              datePublished: PUBLISHED,
              dateModified: PUBLISHED,
              articleSection: "Police Clearance",
            }),
            faqPageSchema(FAQ),
          ]),
        ),
      ],
    },
  }),
  head: ({ loaderData }) => loaderData?.h ?? {},
});

const DOCS: Array<{ doc: string; note: string }> = [
  {
    doc: "Passport or national identity card",
    note: "The name must match every other document in the file, including any recent change of title.",
  },
  {
    doc: "House registration or proof of address in Thailand",
    note: "Foreign applicants normally provide a residence certificate or equivalent proof instead.",
  },
  {
    doc: "Fingerprint forms",
    note: "Taken by a competent authority; unclear prints are the most common reason for a re-take.",
  },
  {
    doc: "Passport photographs",
    note: "Recent, plain background, and to the size the receiving office specifies.",
  },
  {
    doc: "Reason for the request and supporting document",
    note: "Visa application, employer letter, or licensing request, depending on why the certificate is needed.",
  },
  {
    doc: "Name-change or marriage record where relevant",
    note: "Any discrepancy in the file must be explained by an official document, not a covering letter.",
  },
];

const SEQUENCE: string[] = [
  "Confirm the destination requirement, accepted language, and validity window",
  "Prepare identity documents, address proof, and fingerprint forms",
  "Lodge the application with the Royal Thai Police or through an embassy or representative",
  "Collect the issued certificate and check the spelling of every field",
  "Translate the certificate in full, including seal and stamp text",
  "Certify the translation and complete the embassy or apostille step",
];

const MISTAKES: string[] = [
  "Applying too early, so the certificate falls outside the destination validity window",
  "Fingerprints taken without an authority endorsement the Royal Thai Police accepts",
  "Different transliterations of the same name across the documents in one file",
  "Translating before certification, so the certification never appears in the translated set",
  "A power of attorney too narrow to cover collection and later certification",
  "Submitting a photocopy where the destination requires the original",
];

function EnPoliceClearanceGuide() {
  return (
    <article className="mx-auto max-w-3xl px-4 py-12 prose prose-slate dark:prose-invert">
      <nav className="text-sm mb-6 not-prose">
        <Link to="/en" className="text-muted-foreground hover:text-foreground">
          Home
        </Link>
        <span className="mx-2 text-muted-foreground">/</span>
        <Link to="/blog" className="text-muted-foreground hover:text-foreground">
          Blog
        </Link>
      </nav>

      <h1>{TITLE}</h1>
      <BlogHero slug="police-clearance-certificate-guide" locale="en" />
      <p className="answer-summary speakable-summary">
        A Thai police clearance certificate is rarely refused because of its content. It is refused
        because it was issued too early for the destination validity window, translated without the
        seal text, or certified in the wrong order. Fix the submission date first, then work backwards
        through the embassy step, the certified translation, and the police application itself.
      </p>

      <h2>1. Documents to prepare</h2>
      <ul>
        {DOCS.map((d) => (
          <li key={d.doc}>
            <strong>{d.doc}:</strong> {d.note}
          </li>
        ))}
      </ul>

      <h2>2. The correct sequence</h2>
      <ol>
        {SEQUENCE.map((s) => (
          <li key={s}>{s}</li>
        ))}
      </ol>

      <h2>3. Timing and the validity window</h2>
      <p>
        Most destination authorities accept a police clearance certificate only if it was issued
        within three or six months of submission. Because certification and legalisation add further
        working days, the safest plan starts from the submission deadline and allocates time to each
        step in reverse. Applying at the earliest possible moment often produces a document that
        expires before the file is complete.
      </p>

      <h2>4. Applying from outside Thailand</h2>
      <p>
        Applications can be lodged through a Thai embassy or consulate, or through an authorised
        representative in Thailand. Fingerprints are normally taken by a competent authority in your
        country of residence and forwarded to Thailand. Where a representative acts for you, draft the
        power of attorney to cover the request, the collection, the certification, and the embassy
        step in one instrument.
      </p>

      <h2>5. Reasons files are returned</h2>
      <ul>
        {MISTAKES.map((m) => (
          <li key={m}>{m}</li>
        ))}
      </ul>

      <h2>6. Frequently asked questions</h2>
      <dl>
        {FAQ.map((f) => (
          <div key={f.question}>
            <dt>
              <strong>{f.question}</strong>
            </dt>
            <dd>{f.answer}</dd>
          </div>
        ))}
      </dl>

      <h2>7. References and related services</h2>
      <p className="text-sm">
        References:{" "}
        <a href="https://consular.mfa.go.th/en/publicservice/legalization" target="_blank" rel="noreferrer">
          Department of Consular Affairs, Ministry of Foreign Affairs (Thailand)
        </a>{" "}
        ·{" "}
        <a href="https://www.royalthaipolice.go.th/" target="_blank" rel="noreferrer">
          Royal Thai Police
        </a>
      </p>
      <ul className="not-prose mt-4 grid gap-2 text-sm">
        <li>
          <Link to="/en/pcc" className="text-primary underline">
            Police clearance certificate service
          </Link>
        </li>
        <li>
          <Link to="/en/notary" className="text-primary underline">
            Notarial services attorney
          </Link>
        </li>
        <li>
          <Link to="/en/blog/spouse-visa-document-checklist" className="text-primary underline">
            Spouse visa document checklist
          </Link>
        </li>
      </ul>
      <p className="text-sm text-muted-foreground">
        Content reviewed on {PUBLISHED}. This guide is general information for planning purposes and
        is not a determination made on behalf of any authority. Please confirm current requirements
        with the issuing office and the destination authority before you proceed.
      </p>
    </article>
  );
}
